/* VoiceTab — admin upload/list/edit/delete real voice recordings of the
   deceased. NOT to be confused with AI narration.

   Accepts: mp3 / m4a / wav, up to 25 MB. Max 25 recordings per memorial.
   Visibility per recording: public | family | private. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Trash2, Upload, Pencil, Eye, EyeOff, Users, Lock, Circle, Square, X, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { VoicePlayer } from '../../components/VoicePlayer';

const VISIBILITY_OPTIONS = [
  { value: 'public',  label: 'Public — visible on the memorial',          Icon: Eye },
  { value: 'family',  label: 'Family — only signed-in family members',    Icon: Users },
  { value: 'private', label: 'Private — only memorial admins',            Icon: Lock },
];

const ALLOWED_EXT = ['mp3', 'm4a', 'wav', 'webm', 'ogg'];
const MAX_BYTES = 25 * 1024 * 1024;
// iter97.5 — In-browser recorder cap. 15 minutes is plenty for a
// voicemail or blessing; longer recordings should be uploaded as files
// (lets phones / desktop audio software handle long-form recording).
const RECORD_MAX_SECONDS = 15 * 60;

function extOf(name = '') {
  return name.toLowerCase().split('.').pop();
}
function prettyBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function prettyDuration(secs) {
  if (!secs || !Number.isFinite(Number(secs))) return null;
  const s = Math.round(Number(secs));
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

// Read the audio duration client-side so we can store it with the upload.
function probeDuration(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const a = new Audio();
      const done = (v) => { URL.revokeObjectURL(url); resolve(v); };
      a.addEventListener('loadedmetadata', () => {
        done(Number.isFinite(a.duration) && a.duration > 0 ? a.duration : null);
      });
      a.addEventListener('error', () => done(null));
      setTimeout(() => done(null), 8000);
      a.src = url;
    } catch (_) {
      resolve(null);
    }
  });
}

export function VoiceTab({ memorialId }) {
  const [voice, setVoice] = useState([]);
  const [max, setMax] = useState(25);
  const [count, setCount] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Upload-form state
  const [file, setFile] = useState(null);
  const [fileDuration, setFileDuration] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [transcript, setTranscript] = useState('');
  const [recordedDate, setRecordedDate] = useState('');
  const [visibility, setVisibility] = useState('family');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/memorials/${memorialId}/voice`);
      setVoice(data.voice || []);
      setMax(data.max || 25);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [memorialId]);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setFile(null);
    setFileDuration(null);
    setTitle('');
    setDescription('');
    setTranscript('');
    setRecordedDate('');
    setVisibility('family');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = (f) => {
    if (!f) { setFile(null); setFileDuration(null); return; }
    if (!ALLOWED_EXT.includes(extOf(f.name))) {
      toast.error('Please choose an MP3, M4A, or WAV file.');
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(`That file is too large (${prettyBytes(f.size)}). Voice recordings must be 25 MB or smaller.`);
      return;
    }
    setFile(f);
    setFileDuration(null);
    probeDuration(f).then((d) => setFileDuration(d));
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const upload = async () => {
    if (!file) { toast.error('Choose an audio file first.'); return; }
    if (!title.trim()) { toast.error('Please give this recording a title.'); return; }
    if (count >= max) {
      toast.error(`Maximum ${max} voice recordings reached. Delete one to add another.`);
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title.trim());
    fd.append('description', description.trim());
    fd.append('transcript', transcript.trim());
    fd.append('recorded_date', recordedDate);
    fd.append('visibility', visibility);
    if (fileDuration) fd.append('duration_seconds', String(fileDuration));
    setUploading(true);
    try {
      await api.post(`/memorials/${memorialId}/voice`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Voice recording saved.');
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const updateField = async (voiceId, patch) => {
    try {
      await api.patch(`/voice/${voiceId}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (voiceId) => {
    if (!window.confirm('Delete this voice recording? This cannot be undone.')) return;
    try {
      await api.delete(`/voice/${voiceId}`);
      toast.success('Voice recording removed.');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const limitReached = count >= max;

  return (
    <div className="space-y-5" data-testid="voice-tab">

      {/* ----- Upload card ----- */}
      <div className="ml-card">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl text-[hsl(var(--primary))] flex items-center gap-2">
              <Mic className="w-5 h-5" /> Voice Memories
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xl">
              Upload real voice recordings of your loved one — voicemails, birthday messages, blessings, interviews.
              MP3, M4A, or WAV up to 25 MB each. (This is not AI narration.)
            </p>
          </div>
          <span className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--primary)/0.55)]">
            {count} / {max} saved
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <RecordOrUpload
            disabled={limitReached || uploading}
            onFile={handleFile}
            currentFile={file}
            onClear={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
            fileInputRef={fileInputRef}
          />
          <input
            className="ml-input sm:col-span-2"
            placeholder="Title — e.g. Dad's birthday voicemail"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
            disabled={limitReached || uploading}
            data-testid="voice-title-input"
          />
          <input
            type="date" className="ml-input"
            value={recordedDate}
            onChange={(e) => setRecordedDate(e.target.value)}
            disabled={limitReached || uploading}
            data-testid="voice-date-input"
          />
          <select
            className="ml-input"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            disabled={limitReached || uploading}
            data-testid="voice-visibility-input"
          >
            {VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <textarea
            rows={2}
            className="ml-input sm:col-span-2"
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={limitReached || uploading}
            data-testid="voice-desc-input"
          />
          <textarea
            rows={3}
            className="ml-input sm:col-span-2"
            placeholder="Transcript (optional) — type what is said in the recording so visitors can read along"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            disabled={limitReached || uploading}
            data-testid="voice-transcript-input"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={upload}
            disabled={!file || !title.trim() || limitReached || uploading}
            className="ml-btn-primary disabled:opacity-50"
            data-testid="voice-upload-btn"
          >
            <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Save voice recording'}
          </button>
          {limitReached && (
            <span className="text-xs text-[hsl(var(--destructive))]">
              You&apos;ve reached the {max} recording limit. Delete one to add another.
            </span>
          )}
        </div>
      </div>

      {/* ----- List ----- */}
      {voice.length === 0 ? (
        <div className="ml-card text-center py-14" data-testid="voice-empty">
          <Mic className="w-6 h-6 text-[hsl(var(--primary)/0.35)] mx-auto" strokeWidth={1.4} />
          <div className="ml-overline mt-4">Nothing here yet</div>
          <p className="font-serif italic text-[hsl(var(--primary)/0.7)] mt-3">
            Upload your first voice recording above.
          </p>
        </div>
      ) : (
        <div className="ml-card">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Saved recordings</h3>
          <ul className="mt-4 divide-y divide-[hsl(var(--primary)/0.08)] border-y border-[hsl(var(--primary)/0.08)]">
            {voice.map((v) => (
              <VoiceRow
                key={v.voice_id}
                voice={v}
                onUpdate={updateField}
                onRemove={remove}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function VoiceRow({ voice: v, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(v.title || '');
  const [description, setDescription] = useState(v.description || '');
  const [transcript, setTranscript] = useState(v.transcript || '');
  const [recordedDate, setRecordedDate] = useState(v.recorded_date || '');
  const [visibility, setVisibility] = useState(v.visibility || 'family');

  const VisIcon = VISIBILITY_OPTIONS.find((o) => o.value === (v.visibility || 'family'))?.Icon || EyeOff;
  const isPublic = (v.visibility || 'family') === 'public';
  const dur = prettyDuration(v.duration_seconds);

  const save = async () => {
    await onUpdate(v.voice_id, {
      title: title.trim(),
      description: description.trim(),
      transcript: transcript.trim(),
      recorded_date: recordedDate,
      visibility,
    });
    setEditing(false);
  };

  const toggleFeatured = async () => {
    if (!v.is_featured && !isPublic) {
      toast.error('Only public recordings can be featured. Set visibility to Public first.');
      return;
    }
    await onUpdate(v.voice_id, { is_featured: !v.is_featured });
    toast.success(v.is_featured
      ? 'Removed from featured.'
      : 'Featured! This recording now appears near the top of the memorial.');
  };

  // Same-origin file_url (e.g. /api/voice/{id}/file). The <audio> element
  // resolves it against the current page origin which works in both preview
  // and production deployments.
  const audioSrc = `${process.env.REACT_APP_BACKEND_URL || ''}${v.file_url}`;

  return (
    <li className="py-4" data-testid={`voice-row-${v.voice_id}`}>
      {!editing ? (
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--muted))] border border-[hsl(var(--primary)/0.12)] flex items-center justify-center text-[hsl(var(--primary)/0.75)] shrink-0">
            <Mic className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg text-[hsl(var(--primary))] truncate">
              {v.title}
              {v.is_featured && (
                <span className="ml-2 align-middle text-[9px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.45)] rounded-full px-2 py-0.5">
                  Featured
                </span>
              )}
            </div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))] mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <VisIcon className="w-3 h-3" />
                {v.visibility || 'family'}
              </span>
              {v.recorded_date && (
                <>
                  <span className="text-[hsl(var(--primary)/0.25)]">·</span>
                  <span className="normal-case tracking-normal">{v.recorded_date}</span>
                </>
              )}
              {dur && (
                <>
                  <span className="text-[hsl(var(--primary)/0.25)]">·</span>
                  <span className="normal-case tracking-normal font-mono" data-testid={`voice-duration-${v.voice_id}`}>{dur}</span>
                </>
              )}
              {v.size_bytes && (
                <>
                  <span className="text-[hsl(var(--primary)/0.25)]">·</span>
                  <span className="normal-case tracking-normal">{prettyBytes(v.size_bytes)}</span>
                </>
              )}
            </div>
            {v.description && (
              <p className="mt-2 text-sm text-[hsl(var(--primary)/0.75)] italic font-serif">{v.description}</p>
            )}
            {v.transcript && (
              <p className="mt-2 text-xs text-[hsl(var(--primary)/0.6)] line-clamp-2">
                <span className="uppercase tracking-[0.18em] text-[10px] font-semibold">Transcript · </span>
                {v.transcript}
              </p>
            )}
            <div className="mt-3">
              <VoicePlayer
                src={audioSrc}
                seed={v.voice_id}
                durationHint={v.duration_seconds}
                testid={`voice-player-${v.voice_id}`}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={toggleFeatured}
              title={v.is_featured
                ? 'Remove from featured'
                : (isPublic ? 'Feature near the top of the memorial' : 'Make it public first to feature it')}
              className={`text-[11px] tracking-[0.18em] uppercase hover:underline inline-flex items-center gap-1 ${v.is_featured ? 'text-[hsl(var(--accent))]' : 'text-[hsl(var(--primary)/0.55)]'}`}
              data-testid={`voice-feature-btn-${v.voice_id}`}
            >
              <Star className="w-3 h-3" fill={v.is_featured ? 'currentColor' : 'none'} />
              {v.is_featured ? 'Featured' : 'Feature'}
            </button>            <button
              onClick={() => setEditing(true)}
              className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.7)] hover:underline inline-flex items-center gap-1"
              data-testid={`voice-edit-btn-${v.voice_id}`}
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={() => onRemove(v.voice_id)}
              className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--destructive))] hover:underline inline-flex items-center gap-1"
              data-testid={`voice-delete-btn-${v.voice_id}`}
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            className="ml-input sm:col-span-2"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
            data-testid={`voice-edit-title-${v.voice_id}`}
          />
          <input
            type="date" className="ml-input"
            value={recordedDate}
            onChange={(e) => setRecordedDate(e.target.value)}
            data-testid={`voice-edit-date-${v.voice_id}`}
          />
          <select
            className="ml-input"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            data-testid={`voice-edit-visibility-${v.voice_id}`}
          >
            {VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <textarea
            rows={2}
            className="ml-input sm:col-span-2"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid={`voice-edit-desc-${v.voice_id}`}
          />
          <textarea
            rows={3}
            className="ml-input sm:col-span-2"
            placeholder="Transcript (optional) — what is said in the recording"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            data-testid={`voice-edit-transcript-${v.voice_id}`}
          />
          <div className="sm:col-span-2 flex items-center gap-3">
            <button onClick={save} className="ml-btn-primary" data-testid={`voice-edit-save-${v.voice_id}`}>
              Save changes
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setTitle(v.title || ''); setDescription(v.description || '');
                setTranscript(v.transcript || '');
                setRecordedDate(v.recorded_date || ''); setVisibility(v.visibility || 'family');
              }}
              className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.6)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default VoiceTab;

/* ====================================================================== */
/*  RecordOrUpload — pick a file OR record live from the device mic.       */
/*  Uses MediaRecorder for in-browser capture. Falls back to file-only     */
/*  if the browser doesn't support it (older mobile Safari < 14, etc).     */
/* ====================================================================== */

function RecordOrUpload({ disabled, onFile, currentFile, onClear, fileInputRef }) {
  const [mode, setMode] = useState('upload');  // upload | record
  const hasRecorder = typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined'
    && navigator?.mediaDevices?.getUserMedia;

  return (
    <div className="sm:col-span-2 space-y-3" data-testid="voice-source-picker">
      {hasRecorder && (
        <div
          className="inline-flex items-center gap-1.5 rounded-full p-1 border border-[hsl(var(--primary)/0.18)]"
          role="tablist"
        >
          <PickerTab
            active={mode === 'upload'}
            onClick={() => setMode('upload')}
            Icon={Upload}
            label="Upload file"
            testid="voice-mode-upload"
          />
          <PickerTab
            active={mode === 'record'}
            onClick={() => setMode('record')}
            Icon={Mic}
            label="Record now"
            testid="voice-mode-record"
          />
        </div>
      )}

      {mode === 'upload' ? (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.m4a,.wav,.webm,.ogg,audio/mpeg,audio/mp4,audio/wav,audio/x-m4a,audio/webm,audio/ogg"
            className="ml-input w-full"
            onChange={(e) => onFile(e.target.files?.[0])}
            disabled={disabled}
            data-testid="voice-file-input"
          />
          {currentFile && (
            <div className="text-xs text-[hsl(var(--primary)/0.7)] flex items-center gap-2">
              <span className="truncate">Ready: <strong>{currentFile.name}</strong> ({prettyBytes(currentFile.size)})</span>
              <button
                type="button"
                className="text-[hsl(var(--destructive))] hover:underline"
                onClick={onClear}
                data-testid="voice-file-clear"
              >clear</button>
            </div>
          )}
        </>
      ) : (
        <Recorder disabled={disabled} onCapture={onFile} hasRecorder={hasRecorder} />
      )}
    </div>
  );
}

function PickerTab({ active, onClick, Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.22em] uppercase font-medium transition-colors"
      style={{
        backgroundColor: active ? 'hsl(var(--primary))' : 'transparent',
        color: active ? 'hsl(var(--background))' : 'hsl(var(--primary))',
      }}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

function fmtTime(secs) {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function pickRecorderMime() {
  // Prefer webm/opus (Chrome, Edge, Firefox), fall back to mp4 (Safari).
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  if (typeof window === 'undefined' || !window.MediaRecorder?.isTypeSupported) return '';
  return candidates.find((m) => window.MediaRecorder.isTypeSupported(m)) || '';
}

function Recorder({ disabled, onCapture, hasRecorder }) {
  const [state, setState] = useState('idle');     // idle | recording | review
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const streamRef = useRef(null);

  // Clean up object URLs + streams on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (tickRef.current) clearInterval(tickRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (!hasRecorder) {
    return (
      <div className="text-xs text-[hsl(var(--destructive))] p-3 rounded-md border border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.08)]">
        Your browser doesn&apos;t support in-browser recording. Please upload a file instead, or open this page in Chrome / Safari.
      </div>
    );
  }

  const start = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickRecorderMime();
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        const ext = blobType.includes('mp4') ? 'm4a'
                  : blobType.includes('ogg') ? 'ogg'
                  : 'webm';
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setRecordedBlob({ blob, ext });
        setState('review');
        // Free the mic so the OS indicator turns off
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      };

      recorder.start(100);
      setSeconds(0);
      setState('recording');
      tickRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= RECORD_MAX_SECONDS) {
            // Auto-stop at the cap
            try { recorder.stop(); } catch (_) { /* noop */ }
            toast.info(`Reached the ${RECORD_MAX_SECONDS / 60} minute recording limit.`);
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      const msg = e?.message || 'Could not access the microphone.';
      if (e?.name === 'NotAllowedError') {
        toast.error('Microphone permission denied. Allow access in your browser settings and try again.');
      } else if (e?.name === 'NotFoundError') {
        toast.error('No microphone found on this device.');
      } else {
        toast.error(msg);
      }
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && state === 'recording') {
      try { mediaRecorderRef.current.stop(); } catch (_) { /* noop */ }
    }
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setRecordedBlob(null);
    setSeconds(0);
    setState('idle');
  };

  const useRecording = () => {
    if (!recordedBlob) return;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const file = new File(
      [recordedBlob.blob],
      `recording-${stamp}.${recordedBlob.ext}`,
      { type: recordedBlob.blob.type },
    );
    onCapture(file);
    setPreviewUrl(null);
    setRecordedBlob(null);
    setSeconds(0);
    setState('idle');
  };

  if (state === 'recording') {
    return (
      <div
        className="flex items-center gap-3 p-4 rounded-xl border border-[hsl(var(--destructive)/0.45)] bg-[hsl(var(--destructive)/0.06)]"
        data-testid="voice-recording-active"
      >
        <span className="relative flex h-3 w-3" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--destructive))] opacity-60 animate-ping" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[hsl(var(--destructive))]" />
        </span>
        <span className="font-serif text-[hsl(var(--primary))]">Recording…</span>
        <span
          className="ml-auto font-mono text-sm text-[hsl(var(--primary))]"
          data-testid="voice-recording-timer"
        >
          {fmtTime(seconds)} / {fmtTime(RECORD_MAX_SECONDS)}
        </span>
        <button
          type="button"
          onClick={stop}
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--background))] text-[10px] tracking-[0.22em] uppercase font-medium"
          data-testid="voice-stop-btn"
        >
          <Square className="w-3 h-3" fill="currentColor" /> Stop
        </button>
      </div>
    );
  }

  if (state === 'review' && previewUrl) {
    return (
      <div
        className="p-4 rounded-xl border border-[hsl(var(--accent)/0.55)] bg-[hsl(var(--accent)/0.08)] space-y-3"
        data-testid="voice-recording-review"
      >
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.7)]">
          Preview · {fmtTime(seconds)}
        </div>
        <audio controls src={previewUrl} className="w-full" data-testid="voice-recording-preview" />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={useRecording}
            className="ml-btn-primary"
            data-testid="voice-recording-use-btn"
          >
            Use this recording
          </button>
          <button
            type="button"
            onClick={discard}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--destructive))] hover:underline"
            data-testid="voice-recording-discard-btn"
          >
            <X className="w-3 h-3" /> Discard & re-record
          </button>
        </div>
      </div>
    );
  }

  // idle
  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-[hsl(var(--destructive)/0.5)] text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive)/0.06)] disabled:opacity-50 transition-colors"
      data-testid="voice-record-btn"
    >
      <Circle className="w-4 h-4" fill="currentColor" />
      <span className="text-[12px] tracking-[0.22em] uppercase font-semibold">Tap to record</span>
    </button>
  );
}

